import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import AppToaster, { AppToasterProps } from '../../../../components/AppToaster';
import invoiceService, { PatientInvoiceLogEntry } from '../../../../services/http/invoice/invoice.service';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PatientInvoiceLogProps {
    apptId: string | number | null;
}

const PatientInvoiceLog = ({ apptId }: PatientInvoiceLogProps) => {
    const [logs, setLogs] = useState<PatientInvoiceLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [patientEmail, setPatientEmail] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState<Pick<AppToasterProps, 'open' | 'severity' | 'message'>>({
        open: false,
        severity: 'success',
        message: ''
    });

    const fetchInvoice = useCallback(async () => {
        if (!apptId) return;
        setLoading(true);
        try {
            const invoice = await invoiceService.getPatientInvoice(apptId);
            // Pre-fills the dialog. A patient with no email on record just
            // leaves the field empty for the user to type into.
            setPatientEmail(invoice.email);
            setLogs(invoice.logs);
        } catch (error) {
            console.error('Error fetching patient invoice:', error);
        } finally {
            setLoading(false);
        }
    }, [apptId]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);

    const handleOpen = () => {
        setEmail(patientEmail);
        setEmailError('');
        setOpen(true);
    };

    const handleClose = () => {
        if (sending) return;
        setOpen(false);
    };

    const handleSend = async () => {
        const address = email.trim();
        if (!EMAIL_PATTERN.test(address)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        setSending(true);
        try {
            await invoiceService.resendPatientInvoice(apptId as string | number, address);
            setOpen(false);
            setToast({ open: true, severity: 'success', message: `Invoice sent to ${address}` });
            fetchInvoice();
        } catch (error: any) {
            console.error('Error sending patient invoice:', error);
            setToast({
                open: true,
                severity: 'error',
                message: error?.message || 'Unable to send the invoice. Please try again.'
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Stack sx={{ marginTop: '20px', padding: '20px' }}>
            <Typography variant="h5" style={{ marginBottom: '15px' }}>
                Patient Invoice Log
            </Typography>
            <Paper elevation={2} style={{ padding: '20px' }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpen}
                    disabled={!apptId}
                    sx={{ marginBottom: '15px' }}
                >
                    Resend Invoice
                </Button>

                <TableContainer>
                    <Table aria-label="patient invoice log">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.200' }}>
                                <TableCell width="10%"><strong>#</strong></TableCell>
                                <TableCell width="45%"><strong>To</strong></TableCell>
                                <TableCell width="45%"><strong>Send On</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && logs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>No results found.</TableCell>
                                </TableRow>
                            )}
                            {!loading && logs.map((log, index) => (
                                <TableRow key={index} sx={{ bgcolor: index % 2 === 0 ? 'white' : 'grey.200' }}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{log.to}</TableCell>
                                    <TableCell>{log.sendOn}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ paddingRight: '48px' }}>
                    Would you like to send the online consultation invoice to the below email address?
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="flex-start" gap={1} paddingTop="8px">
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            label="Email"
                            placeholder="Enter email"
                            value={email}
                            error={Boolean(emailError)}
                            helperText={emailError}
                            disabled={sending}
                            onChange={(event) => {
                                setEmail(event.target.value);
                                if (emailError) setEmailError('');
                            }}
                        />
                        <Button
                            variant="contained"
                            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                            onClick={handleSend}
                            disabled={sending}
                        >
                            Send
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <AppToaster
                open={toast.open}
                severity={toast.severity}
                message={toast.message}
                duration={5000}
                handleClose={() => setToast((prev) => ({ ...prev, open: false }))}
            />
        </Stack>
    );
};

export default PatientInvoiceLog;
